import { useEffect, useState, useRef } from "react";
import { api, apiErr, fileUrl } from "@/admin/api";
import { PageTitle } from "@/admin/ui";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { ImagePlus, Loader2, Save, Mail, Send, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/admin/AuthContext";

export default function HomepageContent() {
  const { user } = useAuth();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [testEmail, setTestEmail] = useState("");
  const [sendingTest, setSendingTest] = useState(false);
  const [uploadingKey, setUploadingKey] = useState("");
  const fileRef = useRef();
  const logoRef = useRef();
  const heroRef = useRef();

  useEffect(() => {
    api.get("/site-content").then(({ data }) => setForm(data)).catch((e) => toast.error(apiErr(e)));
  }, []);
  useEffect(() => { if (user?.email) setTestEmail(user.email); }, [user]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      set("founder_image", data.url);
      toast.success("Image uploaded — remember to Save");
    } catch (e) { toast.error(apiErr(e)); }
    finally { setUploading(false); }
  };

  const uploadImage = (key) => async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingKey(key);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      set(key, data.url);
      toast.success("Image uploaded — remember to Save");
    } catch (e) { toast.error(apiErr(e)); }
    finally { setUploadingKey(""); }
  };

  const sendTest = async () => {
    if (!testEmail.trim()) { toast.error("Enter an email to send the test to."); return; }
    setSendingTest(true);
    try {
      await api.post("/admin/site-content/test-welcome-email", {
        to: testEmail.trim(),
        subject: form.welcome_email_subject,
        intro: form.welcome_email_intro,
      });
      toast.success(`Test email sent to ${testEmail.trim()}`);
    } catch (e) { toast.error(apiErr(e)); }
    finally { setSendingTest(false); }
  };

  const save = async () => {    setSaving(true);
    try {
      const { data } = await api.patch("/admin/site-content", {
        founder_heading: form.founder_heading,
        founder_name: form.founder_name,
        founder_title: form.founder_title,
        founder_note: form.founder_note,
        founder_image: form.founder_image,
        flights_disclaimer: form.flights_disclaimer,
        insurance_note: form.insurance_note,
        booking_terms: form.booking_terms,
        cancellation_policy: form.cancellation_policy,
        welcome_email_enabled: form.welcome_email_enabled,
        welcome_email_subject: form.welcome_email_subject,
        welcome_email_intro: form.welcome_email_intro,
        welcome_email_logo: form.welcome_email_logo,
        welcome_email_hero: form.welcome_email_hero,
        welcome_email_auto_feature: form.welcome_email_auto_feature,
        lead_form_enabled: form.lead_form_enabled,
        turnstile_site_key: form.turnstile_site_key ?? "",
        ...(form.turnstile_secret_key ? { turnstile_secret_key: form.turnstile_secret_key } : {}),
      });
      setForm({ ...data, turnstile_secret_key: "" });
      toast.success("Homepage content updated");
    } catch (e) { toast.error(apiErr(e)); }
    finally { setSaving(false); }
  };

  if (!form) return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-terracotta" /></div>;

  return (
    <div data-testid="admin-homepage-content">
      <PageTitle title="Homepage Content" subtitle="Update the Founder note and photo shown on your homepage">
        <Button data-testid="content-save" onClick={save} disabled={saving} className="rounded-full bg-terracotta hover:bg-terracotta-hover text-white gap-2">
          {saving ? <Loader2 className="animate-spin" size={16} /> : <><Save size={16} /> Save Changes</>}
        </Button>
      </PageTitle>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-1">
          <div className="bg-white rounded-2xl border border-bone p-5">
            <Label className="text-ink font-medium">Founder Photo</Label>
            <div className="mt-3 aspect-[3/4] rounded-xl overflow-hidden bg-bone">
              {form.founder_image
                ? <img src={fileUrl(form.founder_image)} alt="Founder" className="w-full h-full object-cover" data-testid="content-founder-preview" />
                : <div className="w-full h-full flex items-center justify-center text-ink-soft/40"><ImagePlus size={36} /></div>}
            </div>
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={upload} data-testid="content-image-input" />
            <Button type="button" variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading}
              className="mt-3 w-full rounded-full border-ink/20 gap-2">
              {uploading ? <Loader2 className="animate-spin" size={16} /> : <ImagePlus size={16} />} Upload New Photo
            </Button>
            <p className="text-xs text-ink-soft mt-2">Recommended: portrait orientation, at least 800×1000px.</p>
          </div>
        </div>

        <div className="lg:col-span-2 bg-white rounded-2xl border border-bone p-5 space-y-4">
          <div className="flex flex-col gap-1.5">
            <Label className="text-ink font-medium">Section Heading</Label>
            <Input data-testid="content-heading" value={form.founder_heading || ""} onChange={(e) => set("founder_heading", e.target.value)} className="rounded-xl border-bone" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="flex flex-col gap-1.5">
              <Label className="text-ink font-medium">Founder Name</Label>
              <Input data-testid="content-name" value={form.founder_name || ""} onChange={(e) => set("founder_name", e.target.value)} className="rounded-xl border-bone" />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label className="text-ink font-medium">Founder Title</Label>
              <Input data-testid="content-title" value={form.founder_title || ""} onChange={(e) => set("founder_title", e.target.value)} className="rounded-xl border-bone" />
            </div>
          </div>
          <div className="flex flex-col gap-1.5">
            <Label className="text-ink font-medium">Founder Note</Label>
            <Textarea data-testid="content-note" value={form.founder_note || ""} onChange={(e) => set("founder_note", e.target.value)} className="rounded-xl border-bone min-h-[160px]" />
          </div>
        </div>
      </div>

      <div className="mt-6 bg-white rounded-2xl border border-bone p-5">
        <h2 className="font-serif text-xl font-semibold text-ink">Shared Trip Notes</h2>
        <p className="text-xs text-ink-soft mb-4">Write these once — they appear automatically on every travel offer page (in the "Good to know" section).</p>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="flex flex-col gap-1.5">
            <Label className="text-ink font-medium">Flights disclaimer</Label>
            <Textarea data-testid="content-flights" value={form.flights_disclaimer || ""} onChange={(e) => set("flights_disclaimer", e.target.value)} className="rounded-xl border-bone min-h-[80px]" />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label className="text-ink font-medium">Travel insurance note</Label>
            <Textarea data-testid="content-insurance" value={form.insurance_note || ""} onChange={(e) => set("insurance_note", e.target.value)} className="rounded-xl border-bone min-h-[80px]" />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label className="text-ink font-medium">Booking terms</Label>
            <Textarea data-testid="content-terms" value={form.booking_terms || ""} onChange={(e) => set("booking_terms", e.target.value)} className="rounded-xl border-bone min-h-[80px]" />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label className="text-ink font-medium">Cancellation policy</Label>
            <Textarea data-testid="content-cancellation" value={form.cancellation_policy || ""} onChange={(e) => set("cancellation_policy", e.target.value)} className="rounded-xl border-bone min-h-[80px]" />
          </div>
        </div>
      </div>

      <div className="mt-6 bg-white rounded-2xl border border-bone p-5" data-testid="lead-form-settings">
        <div className="flex items-center justify-between gap-4 mb-1">
          <div className="flex items-center gap-2">
            <ShieldCheck size={18} className="text-terracotta" />
            <h2 className="font-serif text-xl font-semibold text-ink">Travel List Form</h2>
          </div>
          <div className="flex items-center gap-2">
            <Label className="text-sm text-ink-soft">{form.lead_form_enabled !== false ? "Accepting sign-ups" : "Paused"}</Label>
            <Switch data-testid="lead-form-enabled" checked={form.lead_form_enabled !== false} onCheckedChange={(c) => set("lead_form_enabled", c)} />
          </div>
        </div>
        <p className="text-xs text-ink-soft">
          Bot protection is always on: hidden trap field, minimum fill time, per-visitor limits and spam-content checks.
          Caught submissions go to the Spam folder on the Inquiries page and never email you.
          Switch this off to pause the form completely — visitors will see your email and phone instead.
        </p>

        <div className="mt-4 rounded-xl bg-sand/60 border border-bone p-4">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <Label className="text-ink font-medium">Cloudflare Turnstile security check</Label>
            <span data-testid="turnstile-status" className={`text-xs font-semibold px-2.5 py-1 rounded-full ${form.turnstile_configured ? "bg-emerald-100 text-emerald-700" : "bg-bone text-ink-soft"}`}>
              {form.turnstile_configured ? "Active" : "Not set up"}
            </span>
          </div>
          <p className="text-xs text-ink-soft mt-1 mb-3">
            The strongest wall against bots: a small "I'm human" check on the form. Free from Cloudflare — create a Turnstile widget at{" "}
            <a href="https://dash.cloudflare.com/?to=/:account/turnstile" target="_blank" rel="noreferrer" className="text-terracotta underline">dash.cloudflare.com → Turnstile</a>,
            add your domain (kisliving.com), then paste the two keys here. Leave blank to run without it.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="flex flex-col gap-1.5">
              <Label className="text-xs text-ink-soft">Site key (public)</Label>
              <Input data-testid="turnstile-site-key" value={form.turnstile_site_key || ""} onChange={(e) => set("turnstile_site_key", e.target.value)}
                placeholder="0x4AAAAAAA…" className="rounded-xl border-bone bg-white font-mono text-xs" />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label className="text-xs text-ink-soft">Secret key {form.turnstile_configured && <span className="font-normal">(saved — paste again only to change)</span>}</Label>
              <Input data-testid="turnstile-secret-key" type="password" value={form.turnstile_secret_key || ""} onChange={(e) => set("turnstile_secret_key", e.target.value)}
                placeholder={form.turnstile_configured ? "••••••••••••" : "0x4AAAAAAA…"} className="rounded-xl border-bone bg-white font-mono text-xs" />
            </div>
          </div>
          {form.turnstile_configured && (
            <button type="button" data-testid="turnstile-remove" onClick={() => { set("turnstile_site_key", ""); set("turnstile_secret_key", " "); }}
              className="text-xs text-red-500 hover:underline mt-2">Remove Turnstile</button>
          )}
        </div>
      </div>

      <div className="mt-6 bg-white rounded-2xl border border-bone p-5">
        <div className="flex items-center justify-between gap-4 mb-1">
          <div className="flex items-center gap-2">
            <Mail size={18} className="text-terracotta" />
            <h2 className="font-serif text-xl font-semibold text-ink">Welcome Email</h2>
          </div>
          <div className="flex items-center gap-2">
            <Label className="text-sm text-ink-soft">Send automatically</Label>
            <Switch data-testid="welcome-enabled" checked={!!form.welcome_email_enabled} onCheckedChange={(c) => set("welcome_email_enabled", c)} />
          </div>
        </div>
        <p className="text-xs text-ink-soft mb-4">Sent to each new subscriber the moment they join your Travel List. Repeat sign-ups won't get it twice.</p>
        <div className="space-y-4">
          <div className="flex flex-col gap-1.5">
            <Label className="text-ink font-medium">Subject line</Label>
            <Input data-testid="welcome-subject" value={form.welcome_email_subject || ""} onChange={(e) => set("welcome_email_subject", e.target.value)} className="rounded-xl border-bone" />
          </div>

          <div className="flex items-center justify-between rounded-xl bg-sand/60 border border-bone p-3">
            <div>
              <Label className="text-ink font-medium">Auto-feature your latest live trip</Label>
              <p className="text-xs text-ink-soft mt-0.5">Automatically shows your newest live trip's image & title (with a link) so the email always feels current.</p>
            </div>
            <Switch data-testid="welcome-auto-feature" checked={!!form.welcome_email_auto_feature} onCheckedChange={(c) => set("welcome_email_auto_feature", c)} />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="flex flex-col gap-1.5">
              <Label className="text-ink font-medium">Logo (top of email)</Label>
              <div className="flex items-center gap-3">
                {form.welcome_email_logo
                  ? <img src={fileUrl(form.welcome_email_logo)} alt="Logo" data-testid="welcome-logo-preview" className="h-12 max-w-[140px] object-contain rounded-lg border border-bone bg-white p-1" />
                  : <div className="h-12 w-24 rounded-lg border border-dashed border-bone flex items-center justify-center text-ink-soft/40"><ImagePlus size={18} /></div>}
                <input ref={logoRef} type="file" accept="image/*" className="hidden" onChange={uploadImage("welcome_email_logo")} data-testid="welcome-logo-input" />
                <div className="flex flex-col gap-1">
                  <Button type="button" variant="outline" size="sm" onClick={() => logoRef.current?.click()} disabled={uploadingKey === "welcome_email_logo"} className="rounded-full border-ink/20 gap-2">
                    {uploadingKey === "welcome_email_logo" ? <Loader2 className="animate-spin" size={14} /> : <ImagePlus size={14} />} Upload
                  </Button>
                  {form.welcome_email_logo && <button type="button" data-testid="welcome-logo-remove" onClick={() => set("welcome_email_logo", "")} className="text-xs text-red-500 hover:underline">Remove</button>}
                </div>
              </div>
            </div>
            <div className="flex flex-col gap-1.5">
              <Label className="text-ink font-medium">Featured trip image {form.welcome_email_auto_feature && <span className="text-xs text-ink-soft font-normal">(used only when auto-feature is off)</span>}</Label>
              <div className="flex items-center gap-3">
                {form.welcome_email_hero
                  ? <img src={fileUrl(form.welcome_email_hero)} alt="Featured" data-testid="welcome-hero-preview" className="h-12 w-20 object-cover rounded-lg border border-bone" />
                  : <div className="h-12 w-20 rounded-lg border border-dashed border-bone flex items-center justify-center text-ink-soft/40"><ImagePlus size={18} /></div>}
                <input ref={heroRef} type="file" accept="image/*" className="hidden" onChange={uploadImage("welcome_email_hero")} data-testid="welcome-hero-input" />
                <div className="flex flex-col gap-1">
                  <Button type="button" variant="outline" size="sm" onClick={() => heroRef.current?.click()} disabled={uploadingKey === "welcome_email_hero"} className="rounded-full border-ink/20 gap-2">
                    {uploadingKey === "welcome_email_hero" ? <Loader2 className="animate-spin" size={14} /> : <ImagePlus size={14} />} Upload
                  </Button>
                  {form.welcome_email_hero && <button type="button" data-testid="welcome-hero-remove" onClick={() => set("welcome_email_hero", "")} className="text-xs text-red-500 hover:underline">Remove</button>}
                </div>
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-1.5">
            <Label className="text-ink font-medium">Welcome message</Label>
            <Textarea data-testid="welcome-intro" value={form.welcome_email_intro || ""} onChange={(e) => set("welcome_email_intro", e.target.value)} className="rounded-xl border-bone min-h-[120px]" />
            <p className="text-xs text-ink-soft">This appears as the main paragraph. It's signed automatically with your founder name & title above.</p>
          </div>

          <div className="border-t border-bone pt-4">
            <Label className="text-ink font-medium">Preview it in your inbox</Label>
            <p className="text-xs text-ink-soft mb-2">Sends a test using the subject & message above (no need to save first).</p>
            <div className="flex flex-col sm:flex-row gap-2">
              <Input data-testid="welcome-test-email" type="email" value={testEmail} onChange={(e) => setTestEmail(e.target.value)}
                placeholder="you@email.com" className="rounded-xl border-bone flex-1" />
              <Button data-testid="welcome-send-test" type="button" onClick={sendTest} disabled={sendingTest}
                variant="outline" className="rounded-full border-terracotta text-terracotta hover:bg-terracotta/5 gap-2 whitespace-nowrap">
                {sendingTest ? <Loader2 className="animate-spin" size={16} /> : <Send size={16} />} Send me a test
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
